import moment from 'moment';
import { parseUrlFilterParams } from '@intelligence/v3/utils/urlFilterParamsAnalyser';
import {
  getGenreReqParamsForOld,
  getGenreReqParamsForNew,
} from '@intelligence/components/GenreSelectMiddleware/Supports/utils';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { GENRE_SOURCE_MAP, GENRE_SOURCES_NEW } from '@intelligence/components/GenreSelect/constants';

import type { IOptionWithChildren } from '@intelligence/components/GenreSelect/types';

export const formatDateToMMMMYYDD = (date: any) => {
  // date为undefined时，moment会转为当天，不对，返回为空
  const formatDate = moment(date).format('YYYY-MM-DD');
  if (formatDate === 'Invalid date' || !date) {
    return '';
  }
  return formatDate;
};

export interface CountryItemType {
  country_code: string;
  country_name_ch: string;
  country_name_en: string;
  deviation: string;
}

export interface AreaItemtype {
  area_code: string;
  area_name_ch: string;
  area_name_en: string;
  country_set: CountryItemType[];
}
interface FormatCountryItemType extends CountryItemType {
  chText: string;
  enText: string;
  country_name_abbre: string;
}
interface FormatAreaItemType {
  region_abbre: string;
  region_key: string;
  region_name_cn: string;
  region_name_en: string;
  country: FormatCountryItemType[];
}

// region option 格式化
export const formatRegionOptions: (
  options: AreaItemtype[],
  isEn: boolean,
) => Array<FormatAreaItemType> = (options, isEn) => {
  return (options || []).map((item) => {
    const {
      country_set: country,
      area_code: areaCode = '',
      area_name_ch: chText,
      area_name_en: enText,
    } = item;
    const enName = isEn ? enText : chText;
    return {
      // ...item,
      // 没有传code的时候，用英文名称作为abbre和key，如：ua页面
      region_abbre: areaCode.toLowerCase() || enText,
      region_key: areaCode || enText,
      region_name_cn: chText,
      // 临时处理。组件的翻译功能正在添加中，添加完后恢复原代码
      region_name_en: enName,
      // region_name_en: enText,
      country: (country || []).map((it) => {
        const {
          country_code: countryCode = '',
          country_name_ch: chText,
          country_name_en: enText,
        } = it;
        // return {
        //   ...it,
        //   chText,
        //   enText:isEn ? enText : chText,
        //   country_name_abbre: countryCode.toLowerCase(),
        // }
        // 临时处理。组件的翻译功能正在添加中，添加完后恢复原代码
        return {
          ...it,
          country_name_ch: chText,
          country_name_en: isEn ? enText : chText,
          chText,
          enText: isEn ? enText : chText,
          country_name_abbre: countryCode.toLowerCase(),
        };
      }),
    };
  });
};

const getUserInfoGameInfoKey = (userInfo: { userid: string }) => {
  const userId = userInfo?.userid;
  return `dbi-${userId}`;
};

/** 获取filter name 缓存的key */
export const getFilterNameStorageKey = (userInfo: { userid: string }) => {
  return `${getUserInfoGameInfoKey(userInfo)}-filter-name`;
};

/** 获取filter name值 */
export const getFilterName = (userInfo: { userid: string }) => {
  const key = getFilterNameStorageKey(userInfo);
  return sessionStorage.getItem(key);
};

/** 操作filter */
export const saveFilterName = (userInfo: { userid: string }, name?: string) => {
  const key = getFilterNameStorageKey(userInfo);
  if (name) {
    sessionStorage.setItem(key, name);
  } else {
    sessionStorage.removeItem(key);
  }
};

/** 获取filter 值的key */
export const getFilterValueStorageKey = (userInfo: { userid: string }) => {
  return `${getUserInfoGameInfoKey(userInfo)}-filter-value`;
};

/** 获取filter 值 */
export const getFilterValueStorage = (userInfo: { userid: string }) => {
  const key = getFilterValueStorageKey(userInfo);

  try {
    return JSON.parse((window.sessionStorage as any).getItem(key));
  } catch (e) {
    return null;
  }
};

/** 设置filter 值 */
export const saveFilterValueStorage = (userInfo: { userid: string }, data: any) => {
  const key = getFilterValueStorageKey(userInfo);
  if (data) {
    sessionStorage.setItem(key, JSON.stringify(data));
  } else {
    sessionStorage.removeItem(key);
  }
};

export const splitValue = (value: string) => {
  if (typeof value === 'string') {
    return value?.split(',');
  }
  return [];
};

const getGranularity = (type: string) =>
  ({ date: 'daily', week: 'weekly', month: 'monthly', year: 'yearly' }[type] || 'daily');
const getType = (type: string) =>
  ({ date: 'day', week: 'week', month: 'month', year: 'year' }[type] || 'day');

// 这个函数应该没啥用了，因为现在没用到extendFilterType； 但暂时保留
const handleExtendFilterType = (value: any) => {
  const newVal: any = {};
  // extendFilterType为继承特殊类型
  for (const i in value) {
    const extendFilterType = value[i]?.extendFilterType;
    if (extendFilterType && extendFilterType !== 'common') {
      // newVal[`${i}_${extendFilterType}`] = value[i];
      newVal[`${extendFilterType}`] = value[i];
    } else {
      newVal[i] = value[i];
    }
  }
  return newVal;
};

// 对2个国家筛选器值同步(可用于继承、保存)
export const updateSameCountriesFilterValues = (value: any) => {
  const newVal = handleExtendFilterType(value);
  const marketExtendComposed = newVal?.[filterKeyMap.COMPOSED_COUNTRY_SELECT];
  const marketExtendRegion = newVal?.[filterKeyMap.REGION];

  // fromOthersFilter:不是从其他筛选器继承过来的，即自己就是王者，给别人提供数据
  if (marketExtendComposed && !marketExtendComposed?.fromOthersFilter) {
    const markets = marketExtendComposed?.value?.countries || [];
    newVal[filterKeyMap.REGION] = {
      fromOthersFilter: true,
      filterType: 'market',
      region_type: 'market',
      filterRegions: [],
      filterMarkets: markets,
      regions: markets,
      markets,
      realMarkets: markets,
      region_values: markets,
    };
    newVal[filterKeyMap.COMPOSED_COUNTRY_SELECT] = {
      ...newVal[filterKeyMap.COMPOSED_COUNTRY_SELECT],
      fromOthersFilter: false,
    };
  } else if (marketExtendRegion && !marketExtendRegion?.fromOthersFilter) {
    const markets = marketExtendRegion?.realMarkets || marketExtendRegion?.markets || [];
    const isGlobalMarket = markets?.[0] === 'global';
    newVal[filterKeyMap.COMPOSED_COUNTRY_SELECT] = {
      fromOthersFilter: true,
      value: {
        type: markets?.length > 1 || isGlobalMarket ? 'multiple' : 'single',
        countries: isGlobalMarket ? [] : markets,
      },
    };
    newVal[filterKeyMap.REGION] = {
      ...newVal[filterKeyMap.REGION],
      fromOthersFilter: false,
    };
  }
  return newVal;
};

const formatRegionPageFunc = (regionFilterValues: {
  markets: string[];
  regions?: string[];
  type: string;
  extendFilterType?: string;
  realMarkets?: string[];
}) => {
  const globalType = regionFilterValues?.markets
    ? regionFilterValues?.markets[0] === 'global' ||
      regionFilterValues?.markets[0] === 'global_wo_china'
    : false;
  const regionType = globalType ? 'region' : regionFilterValues?.type;
  const regionsValue = globalType
    ? regionFilterValues?.markets
    : regionType === 'region'
    ? regionFilterValues?.regions
    : regionFilterValues?.markets;

  // apiValue[filterKeyMap.REGION] = {
  const params = {
    extendFilterType: regionFilterValues?.extendFilterType,
    filterType: regionFilterValues?.type,
    filterRegions: regionFilterValues?.regions,
    filterMarkets: regionFilterValues?.markets,
    region_type: regionType,
    regions: regionsValue,
    markets: regionFilterValues?.markets,
    realMarkets: regionFilterValues?.realMarkets,
    region_values: regionType === 'markets' ? regionFilterValues?.markets : regionsValue,
    /** 用于url路径 */
    urlValueFormat: {
      [filterKeyMap.REGION]: regionFilterValues?.realMarkets || regionFilterValues?.markets,
    },
  };
  return params;
};
// region=cd,ng,za
function formatRegionUrlFunc(value: string) {
  if (value?.toLocaleLowerCase() === 'all') {
    return [];
  }
  return formatRegionPageFunc({
    markets: splitValue(value) || [],
    // regions: string[];
    type: 'market',
    realMarkets: splitValue(value) || [],
  });
}
// region=cd,ng,za
const formatContrySelectUrlFunc = (value: string) => {
  const markets = splitValue(value);
  const params = {
    // fromOthersFilter: true,
    value: {
      type: markets?.length > 1 ? 'multiple' : 'single',
      countries: markets,
    },
  };
  return params;
};

export const formatRangeTimePageFunc = (timerFilterValues: {
  type: string; // date , week, month
  value: moment.Moment[];
  extendFilterType?: string;
}) => {
  // console.log(timerFilterValues)
  const granularity = getGranularity(timerFilterValues?.type);
  const timeType = getType(timerFilterValues?.type);

  // 月粒度时，数据给到 1号-31号这种格式
  const startMoment =
    timerFilterValues?.value?.[0] && timeType === 'month'
      ? moment(timerFilterValues?.value?.[0]).startOf('month')
      : timerFilterValues?.value?.[0];
  const endMoment =
    timerFilterValues?.value?.[1] && timeType === 'month'
      ? moment(timerFilterValues?.value?.[1]).endOf('month')
      : timerFilterValues?.value?.[1];

  const startDate = formatDateToMMMMYYDD(startMoment);
  const endDate = formatDateToMMMMYYDD(endMoment);
  let params = {};
  params = {
    ...timerFilterValues,
    extendFilterType: timerFilterValues?.extendFilterType,
    filterType: timerFilterValues?.type,
    filterValue: timerFilterValues?.value,
    time_type: timeType,
    granularity,
    start_date: startDate,
    end_date: endDate,
    urlValueFormat: { [filterKeyMap.RANGE_TIME]: [timeType, startDate, endDate] },
    // urlValueFormat:{
    //   timeType:granularity
    //   start:startDate,
    //   end:endDate,
    // }
  };
  return params;
};
// rangeTime=month,2023-11-01,2023-11-30
export const formatRangeTimeUrlFunc = (value: string) => {
  let [type, start, end] = splitValue(value);
  const timeType = type?.toLocaleLowerCase();
  if (['day', 'date', 'week', 'month']?.includes(timeType)) {
    if (!end) {
      // 没有输入结束时间则代表单天、月
      end = start;
    }
    if (moment(start).isValid() && moment(end).isValid() && moment(start) <= moment(end)) {
      let value = [moment(start), moment(end)];
      if (timeType === 'month') {
        value = [moment(start).startOf('month'), moment(end).endOf('month')];
      }
      return formatRangeTimePageFunc({
        type: timeType === 'day' ? 'date' : timeType,
        value,
      });
    }
  }
  return {};
};

const formatReleaseTimePageFunc = (releaseTimeFilterValues: {
  type: string; // date , week, month
  value: moment.Moment[];
  extendFilterType?: string;
}) => {
  let params = {};
  // 处理ReleaseTime
  if (releaseTimeFilterValues) {
    const releaseStart = formatDateToMMMMYYDD(releaseTimeFilterValues?.value?.[0]);
    const releaseEnd = formatDateToMMMMYYDD(releaseTimeFilterValues?.value?.[1]);
    params = {
      extendFilterType: releaseTimeFilterValues?.extendFilterType,
      filterValue: releaseTimeFilterValues,
      start_release_time: releaseStart,
      end_release_time: releaseEnd,
      value: releaseTimeFilterValues?.value,
      urlValueFormat: { [filterKeyMap.RELEASE_TIME]: [releaseStart, releaseEnd] },
    };
  }
  return params;
};
// releaseTime=2023-12-05,2023-12-06
export const formatReleaseTimeUrlFunc = (value: string) => {
  const [start, end] = splitValue(value);
  if (moment(start).isValid() && moment(end).isValid() && moment(start) <= moment(end)) {
    const value = [moment(start), moment(end)];
    return formatReleaseTimePageFunc({
      type: 'date',
      value,
    });
  }
  return {};
};

export const formatGenerePageFunc = (
  genreFilterValues: {
    source: string;
    mainGenres: any[];
    subGenres: any[];
    level3Genres?: any[];
    level4Genres?: any[];
    level5Genres?: any[];
    extendFilterType?: string;
    options: IOptionWithChildren[];
  },
  filterKey?: string,
  filterInput?: any | null,
) => {
  const { isStdGenreValuesHandler } = filterInput || {};
  const genreParams = {
    source: genreFilterValues?.source,
    mainGenres: genreFilterValues?.mainGenres,
    subGenres: genreFilterValues?.subGenres,
    options: genreFilterValues?.options,
    level3Genres: genreFilterValues?.level3Genres,
    level4Genres: genreFilterValues?.level4Genres,
    level5Genres: genreFilterValues?.level5Genres,
  };
  let params: any = {};
  if (!!isStdGenreValuesHandler) {
    const genreVal = getGenreReqParamsForNew(genreParams);
    const genreSource = genreVal?.source;
    const genres = genreVal?.genres || [];

    params = {
      ...genreFilterValues,
      extendFilterType: genreFilterValues?.extendFilterType,
      genretor: genreFilterValues,
      genre_source: genreSource,
      iegg_genre_req: genreSource === GENRE_SOURCE_MAP.IEGG ? genres : [],
      iegg_genre: genreSource !== GENRE_SOURCE_MAP.IEGG ? [] : genres,
      sensortower_genre: genreSource !== GENRE_SOURCE_MAP.SENSORTOWER ? [] : genres,
      appmagic_genre: genreSource !== GENRE_SOURCE_MAP.APP_MAGIC ? [] : genres,
      appannie_genre: genreSource !== GENRE_SOURCE_MAP.APP_ANNIE ? [] : genres,
    };
  } else if (genreFilterValues?.source === GENRE_SOURCE_MAP.IEGG) {
    const genreVal = getGenreReqParamsForNew(genreParams);

    params = {
      ...genreFilterValues,
      extendFilterType: genreFilterValues?.extendFilterType,
      genretor: genreFilterValues,
      genre: undefined,
      sub_genre: undefined,
      genre_source: genreVal?.source,
      iegg_genre_req: genreVal?.genres,
    };
  } else if (genreFilterValues?.source === GENRE_SOURCE_MAP.MINIGAME) {
    const genreVal = getGenreReqParamsForNew(genreParams);

    params = {
      ...genreFilterValues,
      extendFilterType: genreFilterValues?.extendFilterType,
      genretor: genreFilterValues,
      genre: undefined,
      sub_genre: undefined,
      genre_source: genreVal?.source,
      minigame_genre: genreVal?.genres,
    };
  } else {
    const genreVal = getGenreReqParamsForOld(genreParams);
    params = {
      ...genreFilterValues,
      extendFilterType: genreFilterValues?.extendFilterType,
      genretor: genreFilterValues,
      genre: genreVal?.mainGenres,
      sub_genre: genreVal?.subGenres,
      // genre_source: undefined,
      genre_source: genreVal?.source,
      iegg_genre_req: undefined,
    };
  }
  params.urlValueFormat = {
    [filterKeyMap.GENRETOR]: genreFilterValues?.source,
    maingenres: genreFilterValues?.mainGenres?.map((t) => t?.value),
    subgenres: genreFilterValues?.subGenres?.map((t) => `${t?.parentValue}_${t?.value}`),
  };
  return params;
};
// iegg例子：genretor=iegg&maingenres=Action&subgenres=Action_2D%20Fighter
// annie例子：genretor=annie&maingenres=Action&subgenres=Action_Asymmetr
const formatGenereUrlFunc = (value: string, query: any) => {
  // 品类问题： 1 字符串过长； 2主品类勾选的全选与半选问题 3 品类特殊字符串过多，识别出来值可能不正确
  const mainArr = query?.maingenres?.split(',');
  const mainGenresObj = mainArr?.map((t: string) => {
    return {
      name: t,
      value: t,
      // 因url上面如果输入的话不会全选，暂时这么处理；
      // 待主品类方法可自动判断全选半选这里也不起作用了，不影响
      checked: false,
      indeterminate: true,
    };
  });
  const subArr = query?.subgenres?.split(',');
  const subGenresObj = subArr?.map((subGenres: string) => {
    let mainValue = '';
    let subValue = '';
    mainArr?.forEach((mainGenres: string) => {
      const mainSplit = `${mainGenres}_`;
      if (subGenres?.startsWith(mainSplit)) {
        mainValue = mainGenres;
        subValue = subGenres.split(mainSplit)[1];
      }
    });
    return {
      parentValue: mainValue,
      name: subValue,
      value: subValue,
      checked: true,
    };
  });
  const params = formatGenerePageFunc({
    source: GENRE_SOURCES_NEW.includes(value) ? value : GENRE_SOURCE_MAP.IEGG,
    mainGenres: mainGenresObj,
    subGenres: subGenresObj,
    options: [],
  });
  return params;
};

const formatCommonPageFunc = (filterValues: { value: any }, key: string) => {
  const input = filterValues?.value;
  const valueArr = Array.isArray(input) ? input : [input];
  return {
    ...filterValues,
    urlValueFormat: { [key]: valueArr },
  };
};
// platForm=appstore,googleplay
// dataSource=appannie,sensortower,ogdb
const formatCommonUrlFunc = (value: string, key: string) => {
  return formatCommonPageFunc({ value: splitValue(value) }, key);
};

export const formatRevenueRangePageFunc = (
  revenueRangeFilterValues: { value: { min: number; max: number } },
  key: string,
) => {
  return {
    ...revenueRangeFilterValues,
    urlValueFormat: {
      [key]: [revenueRangeFilterValues?.value?.min, revenueRangeFilterValues?.value?.max],
    },
  };
};
// revenueRange=20000,250000
const formatRevenueRangeURLFunc = (value: string, key: string) => {
  const [min, max] = splitValue(value);
  const minNumber = Number(min);
  const maxNumber = Number(max);
  if (!isNaN(minNumber) && !isNaN(maxNumber) && min <= max && minNumber >= 0) {
    return formatRevenueRangePageFunc({ value: { min: minNumber, max: maxNumber } }, key);
  }
};
const formatRevenueGrowthPageFunc = (filterValues: { value: { opt: string; value: number } }) => {
  return {
    ...filterValues,
    urlValueFormat: {
      [filterKeyMap.REVENUE_GROWTH]: [filterValues?.value?.opt, filterValues?.value?.value],
    },
  };
};
// revenueGrowth=<,4
const formatRevenueGrowthUrlFunc = (value: string) => {
  const [opt, numStr] = splitValue(value);
  if (['<', '<=', '>', '>=', '='].includes(opt) && !isNaN(Number(numStr))) {
    return formatRevenueGrowthPageFunc({
      value: {
        opt,
        value: Number(numStr),
      },
    });
  }
};

const iconNorbleCheckboxUrlFunc = (value: string, key: string) => {
  return formatCommonPageFunc(
    {
      value: value === 'true',
    },
    key,
  );
};

export const pageParamsFormatMap: { [key: string]: any } = {
  [filterKeyMap.REGION]: formatRegionPageFunc,
  [filterKeyMap.RANGE_TIME]: formatRangeTimePageFunc,
  [filterKeyMap.GENRETOR]: formatGenerePageFunc,
  [filterKeyMap.GENRETOR_WITH_SEGMENT]: formatGenerePageFunc,
  [filterKeyMap.PLATFORM]: formatCommonPageFunc,
  [filterKeyMap.DATASOURCE]: formatCommonPageFunc,
  [filterKeyMap.REVENUE_RANGE]: formatRevenueRangePageFunc,
  [filterKeyMap.ARPU_RANGE]: formatRevenueRangePageFunc,
  [filterKeyMap.ACTIVE_USERS_RANGE]: formatRevenueRangePageFunc,
  [filterKeyMap.DOWNLOADS_RANGE]: formatRevenueRangePageFunc,
  [filterKeyMap.RPD_RANGE]: formatRevenueRangePageFunc,
  ...filterKeyMap.assignFilterKeys(
    filterKeyMap.RETENTION_D_RANGE_KEYS,
    formatRevenueRangePageFunc,
  ),
  [filterKeyMap.REVENUE_GROWTH]: formatRevenueGrowthPageFunc,
  [filterKeyMap.ICONABLE_CHECKBOX_SELECT]: formatCommonPageFunc,
  [filterKeyMap.HEAT_MAP]: formatCommonPageFunc,
  [filterKeyMap.SHOW_TOP_COUNTRIES]: formatCommonPageFunc,
  [filterKeyMap.RELEASE_TIME]: formatReleaseTimePageFunc,
  [filterKeyMap.PCU_RANGE]: formatRevenueRangePageFunc,
  [filterKeyMap.DIGITAL_RANGE]: formatRevenueRangePageFunc,
  [filterKeyMap.PHYSICAL_RANGE]: formatRevenueRangePageFunc,
  [filterKeyMap.PRICE_RANGE]: formatRevenueRangePageFunc,
  [filterKeyMap.PREMIUM_RANGE]: formatRevenueRangePageFunc,
  [filterKeyMap.METRICTABLE_DEVICE]: formatCommonPageFunc,
  [filterKeyMap.PHYSICAL_DEVICE]: formatCommonPageFunc,
  [filterKeyMap.DIGITAL_DEVICE]: formatCommonPageFunc,
  [filterKeyMap.SENSOR_TOWER_TAG_ART_STYLE]: formatCommonPageFunc,
  [filterKeyMap.SENSOR_TOWER_TAG_CAMERA_POV]: formatCommonPageFunc,
  [filterKeyMap.SENSOR_TOWER_TAG_PRODUCT_MODEL]: formatCommonPageFunc,
  [filterKeyMap.SENSOR_TOWER_TAG_SETTING]: formatCommonPageFunc,
  [filterKeyMap.SENSOR_TOWER_TAG_TAGS]: formatCommonPageFunc,
  [filterKeyMap.SENSOR_TOWER_TAG_THEME]: formatCommonPageFunc,
  [filterKeyMap.SENSOR_TOWER_TAG_MONETIZATION]: formatCommonPageFunc,
};

export const urlParamsFormatMap: { [key: string]: any } = {
  [filterKeyMap.REGION]: formatRegionUrlFunc,
  [filterKeyMap.COMPOSED_COUNTRY_SELECT]: formatContrySelectUrlFunc,
  [filterKeyMap.RANGE_TIME]: formatRangeTimeUrlFunc,
  [filterKeyMap.GENRETOR]: formatGenereUrlFunc,
  [filterKeyMap.PLATFORM]: (value: string) => formatCommonUrlFunc(value, filterKeyMap.PLATFORM),
  [filterKeyMap.DATASOURCE]: (value: string) => formatCommonUrlFunc(value, filterKeyMap.DATASOURCE),
  [filterKeyMap.REVENUE_RANGE]: (value: string) =>
    formatRevenueRangeURLFunc(value, filterKeyMap.REVENUE_RANGE),
  [filterKeyMap.ARPU_RANGE]: (value: string) =>
    formatRevenueRangeURLFunc(value, filterKeyMap.ARPU_RANGE),
  [filterKeyMap.ACTIVE_USERS_RANGE]: (value: string) =>
    formatRevenueRangeURLFunc(value, filterKeyMap.ACTIVE_USERS_RANGE),
  [filterKeyMap.DOWNLOADS_RANGE]: (value: string) =>
    formatRevenueRangeURLFunc(value, filterKeyMap.DOWNLOADS_RANGE),
  [filterKeyMap.RPD_RANGE]: (value: string) =>
    formatRevenueRangeURLFunc(value, filterKeyMap.RPD_RANGE),
  ...filterKeyMap.RETENTION_D_RANGE_KEYS.reduce((acc, key) => {
    acc[key] = (value: string) => formatRevenueRangeURLFunc(value, key);
    return acc;
  }, {} as Record<string, (value: string) => string>),
  [filterKeyMap.REVENUE_GROWTH]: formatRevenueGrowthUrlFunc,
  [filterKeyMap.ICONABLE_CHECKBOX_SELECT]: (value: string) =>
    formatCommonUrlFunc(value, filterKeyMap.ICONABLE_CHECKBOX_SELECT),
  [filterKeyMap.HEAT_MAP]: (value: string) =>
    iconNorbleCheckboxUrlFunc(value, filterKeyMap.HEAT_MAP),
  [filterKeyMap.SHOW_TOP_COUNTRIES]: (value: string) =>
    iconNorbleCheckboxUrlFunc(value, filterKeyMap.SHOW_TOP_COUNTRIES),
  [filterKeyMap.RELEASE_TIME]: formatReleaseTimeUrlFunc,
  [filterKeyMap.PCU_RANGE]: (value: string) =>
    formatRevenueRangeURLFunc(value, filterKeyMap.PCU_RANGE),
  [filterKeyMap.DIGITAL_RANGE]: (value: string) =>
    formatRevenueRangeURLFunc(value, filterKeyMap.DIGITAL_RANGE),
  [filterKeyMap.PHYSICAL_RANGE]: (value: string) =>
    formatRevenueRangeURLFunc(value, filterKeyMap.PHYSICAL_RANGE),
  [filterKeyMap.PRICE_RANGE]: (value: string) =>
    formatRevenueRangeURLFunc(value, filterKeyMap.PRICE_RANGE),
  [filterKeyMap.PREMIUM_RANGE]: (value: string) =>
    formatRevenueRangeURLFunc(value, filterKeyMap.PREMIUM_RANGE),
  [filterKeyMap.METRICTABLE_DEVICE]: (value: string) =>
    formatCommonUrlFunc(value, filterKeyMap.METRICTABLE_DEVICE),
  [filterKeyMap.PHYSICAL_DEVICE]: (value: string) =>
    formatCommonUrlFunc(value, filterKeyMap.PHYSICAL_DEVICE),
  [filterKeyMap.DIGITAL_DEVICE]: (value: string) =>
    formatCommonUrlFunc(value, filterKeyMap.DIGITAL_DEVICE),
  [filterKeyMap.SENSOR_TOWER_TAG_ART_STYLE]: (value: string) =>
    formatCommonUrlFunc(value, filterKeyMap.SENSOR_TOWER_TAG_ART_STYLE),
  [filterKeyMap.SENSOR_TOWER_TAG_CAMERA_POV]: (value: string) =>
    formatCommonUrlFunc(value, filterKeyMap.SENSOR_TOWER_TAG_CAMERA_POV),
  [filterKeyMap.SENSOR_TOWER_TAG_PRODUCT_MODEL]: (value: string) =>
    formatCommonUrlFunc(value, filterKeyMap.SENSOR_TOWER_TAG_PRODUCT_MODEL),
  [filterKeyMap.SENSOR_TOWER_TAG_SETTING]: (value: string) =>
    formatCommonUrlFunc(value, filterKeyMap.SENSOR_TOWER_TAG_SETTING),
  [filterKeyMap.SENSOR_TOWER_TAG_TAGS]: (value: string) =>
    formatCommonUrlFunc(value, filterKeyMap.SENSOR_TOWER_TAG_TAGS),
  [filterKeyMap.SENSOR_TOWER_TAG_THEME]: (value: string) =>
    formatCommonUrlFunc(value, filterKeyMap.SENSOR_TOWER_TAG_THEME),
  [filterKeyMap.SENSOR_TOWER_TAG_MONETIZATION]: (value: string) =>
    formatCommonUrlFunc(value, filterKeyMap.SENSOR_TOWER_TAG_MONETIZATION),
};

export const onFilterConfirm = (value: any, inputList?: any[] | null) => {
  try {
    // 将国家筛选器转换一下
    const apiValue = updateSameCountriesFilterValues(value);
    // 获取filterKeyMap的键
    const keys = Object.keys(pageParamsFormatMap);

    keys.forEach((filterKey: string) => {
      const filterValues = value[filterKey];
      if (filterValues) {
        const filterInput = inputList?.find((item) => item?.key === filterKey);
        // 使用可选链和函数调用确定api值，如果不在pageParamsFormatMap中，值将为undefined
        apiValue[filterKey] = pageParamsFormatMap[filterKey]?.(filterValues, filterKey, filterInput);
      }
    });

    const timerFilterValues = value[filterKeyMap.RANGE_TIME];
    /** 处理时间数据 */
    if (timerFilterValues) {
      const timeParams = apiValue[filterKeyMap.RANGE_TIME];
      // 同时配置TIME_TYPE筛选器（Overview页面）用于继承
      apiValue[filterKeyMap.TIME_TYPE] = {
        // extendFilterType: dateTypeFilter?.extendFilterType,
        ...apiValue[filterKeyMap.TIME_TYPE],
        value: timeParams?.time_type,
        granularity: timeParams?.granularity,
      };
    }

    // 处理日期粒度类型数据(overview页面才用到的筛选器，但要处理继承)
    const dateTypeFilter = value[filterKeyMap.TIME_TYPE];
    const dateTypeFilterValue = dateTypeFilter?.value;
    if (dateTypeFilter) {
      /** 把时间筛选器的粒度值也设置为时间切换的粒度值 */
      const timeType = getType(dateTypeFilterValue);
      apiValue[filterKeyMap.RANGE_TIME] = {
        extendFilterType: timerFilterValues?.extendFilterType,
        filterType: dateTypeFilterValue,
        filterValue: [],
        time_type: timeType,
        granularity: getGranularity(dateTypeFilterValue),
        start_date: '',
        end_date: '',
      };

      apiValue[filterKeyMap.TIME_TYPE] = {
        extendFilterType: dateTypeFilter?.extendFilterType,
        ...apiValue[filterKeyMap.TIME_TYPE],
        granularity: getGranularity(dateTypeFilterValue),
      };
    }
    return {
      error: false,
      value /** 筛选器数据 */,
      apiValue /** 筛选器转换后的数据 */,
    };
    // onConfirm(value /**筛选器数据 */, apiValue /**筛选器转换后的数据 */);
  } catch (err) {
    console.error(err);
    return { error: true };
  }
};

// 声明一个名为 getDbiUrlParams 的函数，接收一个参数，命名为 history
export function getDbiUrlParams(history: any, options?: { genreOptionsMap?: any; }) {
  const { query } = history.location || {};
  if (typeof query !== 'object') {
    return {};
  }
  let realQuery = query;
  if (Object.keys(query)?.includes(filterKeyMap.REGION)) {
    realQuery = {
      ...realQuery,
      [filterKeyMap.COMPOSED_COUNTRY_SELECT]: query[filterKeyMap.REGION],
    };
  }
  const obj = Object.keys(realQuery).reduce((pre: any, cur: string) => {
    // 获取对应 key 的格式化函数
    const formatFunc = urlParamsFormatMap[cur];
    // 获取当前 key 的值
    const value = realQuery[cur];
    if (value && formatFunc) {
      // 对当前值进行格式化
      const formatValue = formatFunc(value, realQuery);
      // 直接将格式化后的值作为新的键值对合并到上一个 reduce 的结果中
      return { ...pre, [cur]: formatValue };
    }
    return pre;
  }, {}); // 初始化值为空对象

  // 开启url参数设置逻辑
  if (query?.use_url_filter) {
    // 使用新的 URL 参数解析逻辑（从 CasualGenreProfile 跳转）
    const { filterParams: urlFilterParams, customParams: urlCustomParamsParsed } = parseUrlFilterParams(query, {
      genreOptionsMap: options?.genreOptionsMap,
    });

    Object.assign(obj, urlFilterParams);
  }

  return obj;
}

// 将对象转为链接形式
function objToUrl(
  query: { [key: string]: Array<any> | string },
  keyMap: { [key: string]: string } = {},
) {
  const url = [];
  for (const key in query) {
    const element = query[key];
    const newKey = keyMap[key] || key;
    if (element?.length) {
      url.push(`${newKey}=${Array.isArray(element) ? element.join(',') : element}`);
    }
  }

  return url.join('&');
}

export const setGameInfoToUrl = (query: object) => {
  const { pathname } = location;
  const newPathname = pathname.endsWith('/')
    ? pathname.substring(0, pathname.length - 1)
    : pathname;
  const newUrl = `${newPathname}?${objToUrl({ ...query })}`;

  window.history.replaceState(null, '', newUrl);
};
